// Checkout — wraps the PaymentModal with the createTransaction mutation.
//
// All state for the modal itself (open/close) is controlled by the parent
// (POS route) — Checkout is a thin presentational/mutational layer that
// calls `onConfirm` and surfaces loading + errors via callbacks.
//
// Pricing/discount/tax/fulfillment state lives in POS (passed in) so the
// Cart panel and the Checkout summary always agree on what's being charged.

import { useCallback } from 'react';
import { PaymentModal } from './PaymentModal';
import { useAuth } from '@/hooks/useAuth';
import { useCart, expandLines, isAssigned } from '@/store/cart';
import { useCreateTransaction } from '@/hooks/useTransactions';
import type {
  Customer,
  DiscountType,
  FulfillmentType,
  PaymentMethod,
  PaymentTerms,
} from '@/types/domain';

export interface CheckoutProps {
  /** Controls modal visibility. */
  open: boolean;
  onClose: () => void;
  eventId: string;
  customer: Customer | null;
  totals: {
    subtotal: number;
    discountAmount: number;
    taxAmount: number;
    grandTotal: number;
  };
  discountType: DiscountType;
  discountValue: number;
  taxEnabled: boolean;
  taxRate: number;
  fulfillment: FulfillmentType;
  /** Promo ids to apply, in the order chosen on the review step. */
  promoIds?: string[];
  /**
   * Vestigial. These once travelled to `create_transaction()` as the cashier
   * to stamp on the invoice; the function now resolves the cashier from the
   * session server-side, so nothing here reads them and passing them has no
   * effect on the saved transaction.
   *
   * They are kept only so `POS.tsx` keeps compiling while the shift banner
   * still holds the values. Delete both — here and at the call site — once
   * that is confirmed to be the only reason they are passed.
   */
  cashierId?: string;
  cashierName?: string | null;
  onSuccess?: (result: {
    id: string;
    invoiceNumber: string;
    paymentTerms: PaymentTerms;
  }) => void;
  onError?: (message: string) => void;
}

export function Checkout({
  open,
  onClose,
  eventId,
  customer,
  totals,
  discountType,
  discountValue,
  taxEnabled,
  taxRate,
  fulfillment,
  promoIds = [],
  onSuccess,
  onError,
}: CheckoutProps) {
  const { user } = useAuth();
  const items = useCart((s) => s.items);
  const clear = useCart((s) => s.clear);
  const createTxn = useCreateTransaction();

  const handleConfirm = useCallback(
    async (payment: {
      paymentMethod: PaymentMethod;
      paidAmount: number;
      changeAmount: number;
      paymentTerms: PaymentTerms;
      dueDate?: number;
      downPayment?: number;
    }) => {
      if (!user) return;
      if (items.length === 0) return;
      const unassigned = items.filter((i) => !isAssigned(i));
      if (unassigned.length > 0) {
        onError?.(`Pilih lot dulu untuk: ${unassigned.map((i) => i.name).join(', ')}`);
        return;
      }
      try {
        // Only product_id and qty go over the wire. Prices, discount, tax and
        // the grand total are all derived server-side from the event's stock
        // allocation, so the numbers on the invoice cannot be influenced from
        // here — the cart's own totals are display-only.
        //
        // The cashier is likewise taken from the session server-side, which is
        // why the cashierId/cashierName props are not read here at all.
        const result = await createTxn.mutateAsync({
          eventId,
          // allocationId is what create_transaction() sells from. productId
          // rides along only so a cart line restored from a pre-lot draft still
          // resolves; the server refuses it when the product has several lots.
          // One line per (product, batch), in cart order — the same expansion
          // the on-screen preview was computed from.
          items: expandLines(items).map((l) => ({
            productId: l.productId,
            allocationId: l.key,
            qty: l.qty,
          })),
          promoIds,
          discountType,
          discountValue,
          taxEnabled,
          taxRate: taxEnabled ? taxRate : 0,
          paymentMethod: payment.paymentMethod,
          changeAmount: payment.changeAmount,
          fulfillment,
          customerId: customer?.id ?? null,
          paymentTerms: payment.paymentTerms,
          dueDate: payment.dueDate ?? null,
          downPayment: payment.downPayment ?? 0,
        });
        clear();
        onSuccess?.({
          id: result.id,
          invoiceNumber: result.invoiceNumber,
          paymentTerms: payment.paymentTerms,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Gagal membuat transaksi';
        onError?.(message);
      }
    },
    // `totals`, `cashierId` and `cashierName` are deliberately absent: the
    // server derives the money and the cashier from the session, so this
    // callback never reads them (see the comment in the body). Listing them
    // only rebuilt the callback on every cart keystroke.
    [
      user,
      items,
      eventId,
      createTxn,
      discountType,
      discountValue,
      taxEnabled,
      taxRate,
      fulfillment,
      promoIds,
      customer,
      clear,
      onSuccess,
      onError,
    ],
  );

  return (
    <PaymentModal
      open={open}
      onClose={() => {
        if (!createTxn.isPending) onClose();
      }}
      grandTotal={totals.grandTotal}
      fulfillment={fulfillment}
      loading={createTxn.isPending}
      hasCustomer={customer != null}
      customerName={customer?.name}
      onConfirm={handleConfirm}
    />
  );
}
